// src/mobile_pages/const/Progress.jsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  User,
  Trash2,
  LayoutTemplate
} from "lucide-react";
import { getProgressList } from "../../api/const";
import { softDeleteDoc } from "../../api/papers";

export default function ProgressList() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // 검색/필터
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title"); // title, created_by, dns
  const [searchTrigger, setSearchTrigger] = useState(0);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 삭제 상태
  const [deleting, setDeleting] = useState(null);

  // 데이터 조회
  const fetchData = async () => {
    if (!projectId) return;
    setLoading(true);

    try {
      const params = {
        project: projectId,
        [searchField]: q || undefined,
        page,
        page_size: pageSize,
      };

      const res = await getProgressList(projectId, params);
      setRows(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) fetchData();
  }, [projectId, page, searchTrigger]);

  // 검색 실행
  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  // 삭제
  const handleDelete = async (e, dns) => {
    e.stopPropagation();
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      setDeleting(dns);
      await softDeleteDoc(dns, true);
      fetchData();
    } catch (err) {
      alert("삭제 실패");
    } finally {
      setDeleting(null);
    }
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(total / pageSize) || 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-8">
        {/* 헤더: 제목 + 새 보고서 버튼 */}
        <header className="mb-4">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">
              주/월간 보고서
            </h1>
            <button
              onClick={() => navigate(`new`)}
              className="flex-shrink-0 flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              <Plus size={16} strokeWidth={2.5} />
              새 문서
            </button>
          </div>
          <p className="mt-1.5 text-[13px] text-slate-500">
            총 {total}건의 보고서가 등록되어 있습니다
          </p>
        </header>

        {/* 템플릿 선택 바로가기 */}
        <button
          onClick={() => navigate(`/project/${projectId}/const/userTemplate`)}
          className="w-full mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-indigo-50">
              <LayoutTemplate size={16} className="text-indigo-500" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-[13px] font-medium text-slate-800 truncate">템플릿 선택</p>
              <p className="text-[11px] text-slate-500 truncate">저장된 템플릿으로 빠르게 작성</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
        </button>

        {/* 검색 영역 */}
        <section className="mb-3">
          <div className="flex items-center gap-1.5 rounded-xl border bg-white px-2 py-2 shadow-sm min-w-0">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="flex-shrink-0 bg-transparent text-[11px] text-slate-600 font-medium outline-none border-r border-slate-200 pr-1"
            >
              <option value="title">제목</option>
              <option value="created_by">작성자</option>
              <option value="dns">DNS</option>
            </select>
            <Search size={14} className="text-slate-400 flex-shrink-0" />
            <input
              className="flex-1 min-w-0 bg-transparent text-[13px] text-slate-800 placeholder-slate-400 outline-none"
              placeholder="검색어 입력"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button
              onClick={handleSearch}
              className="flex-shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 active:scale-95"
            >
              검색
            </button>
          </div>
        </section>

        {/* 목록 */}
        <section className="space-y-3">
          {loading ? (
            <p className="py-12 text-center text-[15px] text-slate-400">
              불러오는 중...
            </p>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-[15px] text-slate-400">
              등록된 보고서가 없습니다
            </p>
          ) : (
            rows.map((row) => (
              <article
                key={row.dns}
                onClick={() => navigate(`${row.dns}/view`)}
                className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm active:scale-[0.99] transition-transform cursor-pointer"
              >
                {/* 상단: 종류 배지 + 삭제 버튼 */}
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${row.doc_type === "weekly"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-purple-100 text-purple-700"
                    }`}>
                    {row.doc_type === "weekly" ? "주간" : "월간"}
                  </span>
                  <button
                    onClick={(e) => handleDelete(e, row.dns)}
                    disabled={deleting === row.dns}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* 제목 */}
                <h3 className="text-[15px] font-semibold text-slate-800 line-clamp-2 mb-2">
                  {row.title || "(제목 없음)"}
                </h3>

                {/* 메타 정보 */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <User size={12} />
                    {row.created_by?.name || "-"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {row.created_at || "-"}
                  </span>
                  {row.is_file && (
                    <span className="flex items-center gap-1">
                      <FileText size={12} />
                      첨부파일
                    </span>
                  )}
                </div>
              </article>
            ))
          )}
        </section>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <nav className="mt-6 flex items-center justify-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => p - 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canPrev
                ? "border-slate-300 text-slate-600 active:scale-95"
                : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors ${page === pageNum
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 active:bg-slate-100"
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canNext
                ? "border-slate-300 text-slate-600 active:scale-95"
                : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronRight size={18} />
            </button>
          </nav>
        )}
      </main>
    </div>
  );
}
